/* global window, document */

import videojs from 'video.js';
import get from 'lodash/get';
import clone from 'lodash/clone';
import merge from 'lodash/merge';
import { captureException } from '@sentry/browser';

import Encore from './Encore';
import VideoEmbed from './VideoEmbed';

/**
 * HTV video player global class.
 */
export default class EncoreInjector {
  constructor(settings) {
    this.instances = [];
    this.settings = settings;

    this.BU = 'HTV';
    this.events = [];
    this.urlList = [];

    this.settings = settings;
    this.loadedEmbeds = [];
    this.recommendations = [];
    this.autoplayCount = 0;
    this.firstEmbedSet = new Set();

    this.infiniteScrollLoader();
  }

  /**
   * Start attaching all the players on the page based on the
   * settings.playerClass.
   */
  initialize() {
    const selector = `.${this.settings.playerClass}`;
    const allPlayerElements = [...document.querySelectorAll(selector)];

    const initializablePlayerElements = allPlayerElements
      .filter(el => !el.classList.contains(this.settings.loadingClass))
      .filter(el => !el.classList.contains(this.settings.loadedClass))
      .filter(el => el.getAttribute('id'))
      .filter(el => el.getAttribute('data-player-id'));

    for (const el of initializablePlayerElements) {
      this.initPlayerElement(el);
    }
  }

  /**
   * Attach a single player object to the specified DOM element.
   *
   * @param el {HTMLElement} The element to which to attach the player.
   */
  initPlayerElement(el) {
    el.classList.add(this.settings.loadingClass);
    this.loadPlayer(el, this.settings)
      .then(player => {
        this.instances.push(player);
        el.classList.add(this.settings.loadedClass);
      })
      .catch(error => {
        captureException(error);
        videojs.log.error(error);
      });
  }

  /**
   * This method is extracted from the above `startup` so that consumers
   * of this library can override it and inject their own bootstrapping
   * process. For example, HDM has multiple embed types, so we'll be
   * using an extension of the base Player class that supports these
   * types.
   *
   * @param {HTMLElement} el The DOM element to which we attach the player.
   * @param {Object} playerSettings The settings the player needs to render.
   */
  loadPlayer(el, playerSettings) {
    const player = new Encore(el, clone(playerSettings));
    return player.load();
  }

  /**
   * Helper method to pause all player instances.
   * @param {String} ignoreInstanceId optional player ID to be ignored and not paused
   */
  pauseAllInstances(ignoreInstanceId) {
    this.instances.forEach(instance => {
      const { player } = instance;
      const { playerElementId } = instance;
      if (playerElementId !== ignoreInstanceId) {
        if (get(player, 'ima.controller.sdkImpl.adsActive', false)) {
          player.ima.pauseAd();
        } else {
          player.pause();
        }
      }
    });
  }

  /**
   * Helper method to find the current sticky player.
   * @return {instance} Encore video player instance.
   */
  getStickyInstance() {
    for (let i = 0; i < this.instances.length; i++) {
      if (this.instances[i].source.isSticky) {
        return this.instances[i];
      }
    }
    return null;
  }

  /**
   * Infinite scroll loader.
   * Periodically checks for yet-to-be-loaded video elements and initializes them.
   */
  infiniteScrollLoader() {
    const url = document.URL;
    if (!this.urlList.includes(url)) {
      this.urlList.push(url);
      this.initialize();
    }
    setTimeout(this.infiniteScrollLoader.bind(this), 1000);
  }

  /**
   * Recommendations loader.
   * Checks the global recommendations object and fetches video embed data.
   * Store a list of loaded embeds, so that only new embed data requested.
   * If loading fails, reset the loaded embeds list.
   */
  loadRecommendations() {
    if (!get(window, 'hearstLumiereRecs.0.embeds')) {
      return;
    }

    const key = 'hearstLumiereWatched';
    const globalRecs = clone(get(window, 'hearstLumiereRecs.0.embeds'));

    let watched = localStorage.getItem(key);
    if (!watched) {
      watched = [];
    } else {
      try {
        watched = JSON.parse(watched);
      } catch (ex) {
        videojs.log.error(ex);
        watched = [];
      }
    }

    this.loadedEmbeds = merge(this.loadedEmbeds, watched);

    const embedsToLoad = globalRecs.filter(item => {
      if (!this.loadedEmbeds.includes(item)) {
        this.loadedEmbeds.push(item);
        return true;
      }
      return false;
    });

    const embedPromises = embedsToLoad.map(item => {
      const videoEmbed = new VideoEmbed(item, this.settings);
      return videoEmbed.load();
    });

    Promise.all(embedPromises)
      .then(result => {
        this.recommendations = merge(this.recommendations, result);
      })
      .catch(() => {
        this.loadedEmbeds = [];
      });
  }

  /**
   * Increments first video embed count.
   */
  incFirstVideoEmbed(id) {
    if (this.firstEmbedSet.size === 0) {
      this.firstEmbedSet.add(id);
    }
  }

  /**
   * Returns whether or not a player ID is the first video embed.
   */
  isFirstVideoEmbed(id) {
    return this.firstEmbedSet.has(id);
  }

  /**
   * Increments global autoplay count.
   */
  incAutoplayCount() {
    this.autoplayCount++;
  }

  /**
   * Resets global autoplay count.
   */
  resetAutoplayCount() {
    this.autoplayCount = 0;
  }
}
